import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LockIcon } from 'lucide-react';
import { Shell, Section, SectionHeading, Eyebrow, GoldRule } from '../../components/ui/Layout';
import { ButtonLink } from '../../components/ui/Button';
import { OpportunityStatusChip, DisclosureChip, Tag } from '../../components/ui/Chips';
import { Placeholder } from '../../components/ui/Placeholder';
import { blockRecord, blockValue, getContentPage } from '../../data/contentPages';
import { services } from '../../data/services';
import { industries } from '../../data/industries';
import { articles } from '../../data/articles';
import { coverageRegions, searchPhilosophy } from '../../data/about';
import { opportunities } from '../../data/opportunities';
import { CONFIDENTIAL_MANDATE_NOTICE } from '../../types/search';
import { seniorityLabels, workArrangementLabels } from '../../utils/labels';
import { formatCompensation, formatShortDate } from '../../utils/format';
import { applyPageMeta, applyStructuredData, organizationSchema } from '../../utils/seo';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/70984bb4-60ff-42ee-8a7b-f319ef1949bf.jpg';
const RESEARCH_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/cd16981c-f0f9-4fde-96c1-a20a2da1c1d0.jpg';
const processSteps = [{
  n: '01',
  title: 'Specification and calibration',
  body: 'What the role actually is, what the organization will trade, and what success looks like in eighteen months.'
}, {
  n: '02',
  title: 'Research and market mapping',
  body: 'The addressable population documented so you can see the boundary of the search, not only its output.'
}, {
  n: '03',
  title: 'Discreet approach',
  body: 'Individual approaches made by the mandate owner. Interest stays confidential to the search.'
}, {
  n: '04',
  title: 'Assessment',
  body: 'Two consultants, one written framework. Candidates assessed against the specification, not against each other.'
}, {
  n: '05',
  title: 'Shortlist presentation',
  body: 'A written submission for each candidate — released only after that candidate authorizes it.'
}, {
  n: '06',
  title: 'Offer and integration',
  body: 'Referencing with consent, offer construction, and support through the first ninety days.'
}];
const candidateSteps = ['Register in a few minutes. No documents are required.', 'Set what you are open to — and what you are not.', 'Review opportunities with their verification status stated plainly.', 'Apply, then track every status change as a dated event.', 'Authorize each presentation separately, or decline without consequence.'];
const employerSteps = ['Submit a search inquiry. It begins a conversation, not a contract.', 'Complete organization and identity verification.', 'Agree the specification in writing before research begins.', 'Receive progress reporting and a documented market map.', 'See only candidates who have authorized presentation to you.'];
export function Home() {
  const page = getContentPage('home');
  const featured = opportunities.filter(o => o.status === 'verified' || o.status === 'confidential_mandate').slice(0, 3);
  const heroRail = opportunities.slice(0, 2);
  const coverageBlock = blockRecord('home', 'coverage.intro');
  const testimonialsBlock = blockRecord('home', 'testimonials.note');
  useEffect(() => {
    if (page) {
      applyPageMeta({
        title: page.seo.title,
        description: page.seo.metaDescription,
        canonicalPath: page.seo.canonicalPath,
        socialImageUrl: page.seo.socialImageUrl
      });
    }
    applyStructuredData(organizationSchema());
  }, [page]);
  return <>
      {/* Hero */}
      <div className="on-dark relative isolate overflow-hidden bg-ink" data-id="element-744" data-mp="2ra5a5pq4uisc">
        <img src={HERO_IMAGE} alt="" width={1280} height={853} loading="eager" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25" data-id="element-745" data-mp="1kta3eyjjvove" />
        <div aria-hidden="true" className="absolute inset-0 bg-ink/70" data-id="element-746" data-mp="33ptvtcvxujv8" />
        <Shell className="relative py-20 md:py-28" data-id="element-747" data-mp="38hbntbfzha42">
          <div className="grid animate-rise-in gap-14 lg:grid-cols-[1.35fr_1fr] lg:gap-20" data-id="element-748" data-mp="38iwgeviqmiza">
            <div data-id="element-749" data-mp="2urrq4k19f31m">
              <GoldRule data-id="element-750" data-mp="2uo3v1gg628ks" />
              <Eyebrow onDark className="mt-5" data-id="element-751" data-mp="2yrin55hdqfel">
                {blockValue('home', 'hero.eyebrow')}
              </Eyebrow>
              <h1 className="mt-4 max-w-2xl font-serif text-[2.25rem] leading-[1.1] tracking-[-0.015em] text-parchment md:text-[3.25rem]" data-id="element-752" data-mp="3mh4l5pm0mol0">
                {blockValue('home', 'hero.headline')}
              </h1>
              <p className="mt-6 max-w-xl font-serif text-[1.0625rem] leading-relaxed text-navy-100 md:text-[1.125rem]" data-id="element-753" data-mp="3dfwmv7ji1nqx">
                {blockValue('home', 'hero.supporting')}
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-754" data-mp="220hoq0fk0pu8">
                <ButtonLink to="/opportunities" size="lg" data-id="element-755" data-mp="270w11uo6uly2">
                  Explore Leadership Opportunities
                </ButtonLink>
                <ButtonLink to="/register" size="lg" variant="onDark" data-id="element-756" data-mp="1yhsdne3cy0sw">
                  Register as an Executive
                </ButtonLink>
                <ButtonLink to="/for-employers" size="lg" variant="onDark" data-id="element-757" data-mp="28hj7reo0adsg">
                  Discuss a Search Mandate
                </ButtonLink>
              </div>
            </div>

            <aside aria-label="Selected mandates" className="lg:border-l lg:border-navy-600 lg:pl-10" data-id="element-758" data-mp="1ft3w92432dlv">
              <Eyebrow onDark data-id="element-759" data-mp="1au142i97c1vb">Selected mandates</Eyebrow>
              <ul className="mt-6 space-y-5" data-id="element-760" data-mp="1avlwo2byhaqj">
                {heroRail.map(o => <li key={o.id} className="border-b border-navy-600 pb-5" data-id="element-761" data-mp="1pjp4zidh0yii">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-762" data-mp="1krxbayz0qco5">
                      <OpportunityStatusChip status={o.status} onDark data-id="element-763" data-mp="x2mh60c6bgiz" />
                      <DisclosureChip disclosure={o.clientDisclosure} onDark data-id="element-764" data-mp="x0ryytsae2il" />
                    </div>
                    <Link to={`/opportunities/${o.slug}`} className="group mt-3 block" data-id="element-765" data-mp="s1fbc6tlcrey">
                      <h2 className="font-serif text-[1.0625rem] leading-snug text-parchment group-hover:text-gold-200" data-id="element-766" data-mp="2s5iw4fy335o9">
                        {o.title}
                      </h2>
                      <p className="mt-1 font-sans text-[0.8125rem] text-navy-200" data-id="element-767" data-mp="31wvln0wetjr4">
                        {o.location} · {seniorityLabels[o.seniority]}
                      </p>
                    </Link>
                  </li>)}
              </ul>
              <Link to="/opportunities" className="mt-5 inline-flex items-center gap-2 font-sans text-[0.8125rem] text-gold transition-colors duration-120 hover:text-gold-200" data-id="element-768" data-mp="2e5huuzdwpw89">
                View all opportunities
                <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-769" data-mp="2n4lfw8wmwlpe" />
              </Link>
            </aside>
          </div>
        </Shell>
      </div>

      <main id="main" data-id="element-770" data-mp="2fflpkcyu2ef">
        {/* Introduction */}
        <Section tone="parchment" labelledBy="intro-heading" data-id="element-771" data-mp="2ia1gyl1az1i">
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20" data-id="element-772" data-mp="cjvzm86vx9fl">
            <div data-id="element-773" data-mp="7l16acbak2h8">
              <GoldRule data-id="element-774" data-mp="7mlznylvdfdi" />
              <h2 id="intro-heading" className="mt-5 font-serif text-[1.75rem] leading-[1.15] text-ink md:text-[2.25rem]" data-id="element-775" data-mp="7ndw1bnajh4u">
                {blockValue('home', 'intro.heading')}
              </h2>
            </div>
            <div className="space-y-5 font-serif text-[1.0625rem] leading-relaxed text-charcoal-700" data-id="element-776" data-mp="3uwk9ysmjnx4l">
              <p data-id="element-777" data-mp="3l077p6cxoay8">{blockValue('home', 'intro.body')}</p>
              <p className="text-charcoal-500" data-id="element-778" data-mp="3kzfl2l1bdnk3">{blockValue('home', 'intro.secondary')}</p>
            </div>
          </div>
        </Section>

        {/* Services */}
        <Section labelledBy="services-heading" data-id="element-779" data-mp="3c00xbtcj7r3a">
          <SectionHeading eyebrow="How we are engaged" title={blockValue('home', 'services.heading')} intro={blockValue('home', 'services.intro')} data-id="element-780" data-mp="3c1c0ps22ewtv" />
          <ul className="mt-12 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3" data-id="element-781" data-mp="1a8xn356k0ej9">
            {services.map(s => <li key={s.slug} className="border-t border-navy-100 pt-5" data-id="element-782" data-mp="1tja0d0luemhb">
                <Link to={`/services/${s.slug}`} className="group block" data-id="element-783" data-mp="1xhptlm8ior36">
                  <h3 className="font-serif text-[1.0625rem] leading-snug text-ink group-hover:text-gold-700" data-id="element-784" data-mp="1jq1kzsxh8kqy">{s.name}</h3>
                  <p className="mt-2 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-785" data-mp="1oocshx0auj60">{s.shortDescription}</p>
                </Link>
              </li>)}
          </ul>
        </Section>

        {/* Industries */}
        <Section tone="ink" labelledBy="industries-heading" data-id="element-786" data-mp="10s8haof146si">
          <SectionHeading onDark eyebrow="Practice depth" title={blockValue('home', 'industries.heading')} intro={blockValue('home', 'industries.intro')} data-id="element-787" data-mp="10ttdeigzrxsh" />
          <ul className="mt-12 grid gap-x-10 gap-y-1 sm:grid-cols-2 lg:grid-cols-3" data-id="element-788" data-mp="2v46nehi1kmk6">
            {industries.map(i => <li key={i.slug} data-id="element-789" data-mp="3a91dmzklhpp6">
                <Link to={`/industries/${i.slug}`} className="flex items-center justify-between gap-4 border-b border-navy-600 py-3.5 font-serif text-[0.9375rem] text-navy-100 transition-colors duration-120 hover:text-gold-200" data-id="element-790" data-mp="3aacfgtuhcndn">
                  {i.name}
                  <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-50" data-id="element-791" data-mp="3ea33wqwjarf0" />
                </Link>
              </li>)}
          </ul>
        </Section>

        {/* Process */}
        <Section labelledBy="process-heading" data-id="element-792" data-mp="3o064bc5zj7nd">
          <SectionHeading eyebrow="Method" title={blockValue('home', 'process.heading')} intro={blockValue('home', 'process.intro')} data-id="element-793" data-mp="5ut3dn8alu9c" />
          <ol className="mt-12 grid gap-x-10 gap-y-9 md:grid-cols-2 lg:grid-cols-3" data-id="element-794" data-mp="1rn44zjll3cd">
            {processSteps.map(s => <li key={s.n} className="border-t border-navy-100 pt-5" data-id="element-795" data-mp="23lciteh66fae">
                <span className="font-sans text-micro tracking-[0.16em] text-gold-700" data-id="element-796" data-mp="23n7792rngpj8">{s.n}</span>
                <h3 className="mt-2 font-serif text-[1.0625rem] text-ink" data-id="element-797" data-mp="19mbxsb8pu88l">{s.title}</h3>
                <p className="mt-2 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-798" data-mp="15p630fwt3dtw">{s.body}</p>
              </li>)}
          </ol>
        </Section>

        {/* Journeys */}
        <Section tone="parchment" labelledBy="journeys-heading" data-id="element-799" data-mp="vr0323uk8hro">
          <SectionHeading eyebrow="Two paths" title={blockValue('home', 'journeys.heading')} data-id="element-800" data-mp="1tbcj7pgdvzv7" />
          <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-801" data-mp="1oem537o53ecf">
            <div className="border-t-2 border-ink pt-6" data-id="element-802" data-mp="1jmb4e2lkroit">
              <h3 className="font-serif text-[1.375rem] text-ink" data-id="element-803" data-mp="1jkzy9w4nq4oj">For executives</h3>
              <p className="mt-3 font-serif text-[1rem] leading-relaxed text-charcoal-500" data-id="element-804" data-mp="1en8939vecwo1">
                {blockValue('home', 'candidateJourney.intro')}
              </p>
              <ol className="mt-6 space-y-3" data-id="element-805" data-mp="27c5isoljll56">
                {candidateSteps.map((s, i) => <li key={s} className="flex gap-4 font-sans text-[0.875rem] leading-relaxed text-charcoal-700" data-id="element-806" data-mp="2eod5n11ty0kq" data-mpi={i}>
                    <span aria-hidden="true" className="font-sans text-micro tracking-[0.16em] text-gold-700" data-id="element-807" data-mp="2jnfxfjssuaar">
                      0{i + 1}
                    </span>
                    {s}
                  </li>)}
              </ol>
              <div className="mt-7 flex flex-wrap gap-3" data-id="element-808" data-mp="2xmflap8hml5v">
                <ButtonLink to="/register" size="sm" data-id="element-809" data-mp="2xlweo4oafj75">
                  Register as an Executive
                </ButtonLink>
                <ButtonLink to="/for-candidates" size="sm" variant="secondary" data-id="element-810" data-mp="32j60lacgxcq8">
                  How it works
                </ButtonLink>
              </div>
            </div>
            <div className="border-t-2 border-ink pt-6" data-id="element-811" data-mp="3cashnnbhm60w">
              <h3 className="font-serif text-[1.375rem] text-ink" data-id="element-812" data-mp="3hayedhwm6ipu">For employers</h3>
              <p className="mt-3 font-serif text-[1rem] leading-relaxed text-charcoal-500" data-id="element-813" data-mp="3lbqr4l4p35qv">
                {blockValue('home', 'employerJourney.intro')}
              </p>
              <ol className="mt-6 space-y-3" data-id="element-814" data-mp="1m55wezk01ep9">
                {employerSteps.map((s, i) => <li key={s} className="flex gap-4 font-sans text-[0.875rem] leading-relaxed text-charcoal-700" data-id="element-815" data-mp="1h62on7cmwudl" data-mpi={i}>
                    <span aria-hidden="true" className="font-sans text-micro tracking-[0.16em] text-gold-700" data-id="element-816" data-mp="1v1eqrzt4dn4j">
                      0{i + 1}
                    </span>
                    {s}
                  </li>)}
              </ol>
              <div className="mt-7 flex flex-wrap gap-3" data-id="element-817" data-mp="1zxvggyfyw9jq">
                <ButtonLink to="/for-employers" size="sm" data-id="element-818" data-mp="2dy69a70u7y7j">
                  Discuss a Search Mandate
                </ButtonLink>
                <ButtonLink to="/services/retained-executive-search" size="sm" variant="secondary" data-id="element-819" data-mp="29xw3ovgyj9t7">
                  Retained search
                </ButtonLink>
              </div>
            </div>
          </div>
        </Section>

        {/* Featured opportunities */}
        <Section labelledBy="opportunities-heading" data-id="element-820" data-mp="5f51c0acvams">
          <SectionHeading eyebrow="Current mandates" title={blockValue('home', 'opportunities.heading')} intro={blockValue('home', 'opportunities.intro')} data-id="element-821" data-mp="5foj9h009z0t" />
          <ul className="mt-12 divide-y divide-navy-100 border-y border-navy-100" data-id="element-822" data-mp="3ru7mchjj5rrz">
            {featured.map(o => <li key={o.id} data-id="element-823" data-mp="ltqzp6c4bq4">
                <Link to={`/opportunities/${o.slug}`} className="group grid gap-4 py-7 lg:grid-cols-[1fr_auto] lg:items-start" data-id="element-824" data-mp="3hvs1uxf0dgrg">
                  <div data-id="element-825" data-mp="3mw65m39ku4jo">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-826" data-mp="3mta5q9e655sl">
                      <OpportunityStatusChip status={o.status} data-id="element-827" data-mp="38wwvnlbys6o5" />
                      <DisclosureChip disclosure={o.clientDisclosure} data-id="element-828" data-mp="3du6lv376gmd1" />
                      <span className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-829" data-mp="3dwja1y3j3syj">{o.referenceNumber}</span>
                    </div>
                    <h3 className="mt-3 font-serif text-[1.25rem] text-ink group-hover:text-gold-700" data-id="element-830" data-mp="1c3lcxq2pye7s">{o.title}</h3>
                    <p className="mt-1.5 font-sans text-[0.875rem] text-charcoal-500" data-id="element-831" data-mp="385ex2vx6d4ly">
                      {o.clientDisclosure === 'disclosed' ? o.disclosedClientName : o.clientDescriptor}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2" data-id="element-832" data-mp="2y6phxawnxy9e">
                      <Tag data-id="element-833" data-mp="2u7gne3tstwcs">{o.location}</Tag>
                      <Tag data-id="element-834" data-mp="2u6fdw3sflhx6">{seniorityLabels[o.seniority]}</Tag>
                      <Tag data-id="element-835" data-mp="2p8nkt6g1vpre">{workArrangementLabels[o.workArrangement]}</Tag>
                      <Tag data-id="element-836" data-mp="2kh5iof2ajj4u">{o.industry}</Tag>
                    </div>
                  </div>
                  <div className="lg:pl-10 lg:text-right" data-id="element-837" data-mp="2fhj70a242ay5">
                    <p className="font-sans text-[0.875rem] text-ink" data-id="element-838" data-mp="2fi2e0vq83exe">{formatCompensation(o.compensation)}</p>
                    <p className="mt-1 font-sans text-[0.75rem] text-charcoal-300" data-id="element-839" data-mp="2agmsl2k4v6eh">
                      {o.compensation.isEstimate ? 'Estimated — research observation' : 'Client-confirmed'}
                    </p>
                    <p className="mt-3 font-sans text-[0.75rem] text-charcoal-300" data-id="element-840" data-mp="gzz81iwbnst0">Posted {formatShortDate(o.postedAt)}</p>
                  </div>
                </Link>
              </li>)}
          </ul>
          <ButtonLink to="/opportunities" variant="secondary" size="sm" className="mt-8" data-id="element-841" data-mp="q0x33bos8pzg">
            View the full directory
          </ButtonLink>
        </Section>

        {/* Confidential search */}
        <Section tone="navy" labelledBy="confidential-heading" data-id="element-842" data-mp="3uf5746ahrp6u">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20" data-id="element-843" data-mp="3ucskvgxpasnz">
            <div data-id="element-844" data-mp="2bm8kby0zrtr">
              <GoldRule data-id="element-845" data-mp="2903ciylxoep" />
              <Eyebrow onDark className="mt-5" data-id="element-846" data-mp="7a5u4b8pj1ll">
                Confidentiality
              </Eyebrow>
              <h2 id="confidential-heading" className="mt-3 font-serif text-[1.75rem] leading-[1.15] text-parchment md:text-[2.25rem]" data-id="element-847" data-mp="c0mbg6fnf7d8">
                {blockValue('home', 'confidential.heading')}
              </h2>
            </div>
            <div className="space-y-6" data-id="element-848" data-mp="3foxsk1nluqbf">
              <p className="font-serif text-[1.0625rem] leading-relaxed text-navy-100" data-id="element-849" data-mp="3fnmm1u2rz4gm">
                {blockValue('home', 'confidential.body')}
              </p>
              <div className="border-l-2 border-gold pl-5" data-id="element-850" data-mp="hvmlk14yuuwq">
                <p className="flex items-start gap-3 font-sans text-[0.875rem] leading-relaxed text-gold-200" data-id="element-851" data-mp="vnkmhooixkht">
                  <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" data-id="element-852" data-mp="rlz27i9nu3xl" />
                  {blockValue('home', 'confidential.rule')}
                </p>
              </div>
              <div className="border border-navy-600 bg-navy-800 p-6" data-id="element-853" data-mp="3v37j9zwkczr9">
                <Eyebrow onDark data-id="element-854" data-mp="3v50t1o2h403h">Shown on every confidential mandate</Eyebrow>
                <p className="mt-3 font-serif text-[0.9375rem] leading-relaxed text-navy-100" data-id="element-855" data-mp="cyw3j89lpp8o">
                  “{CONFIDENTIAL_MANDATE_NOTICE}”
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Coverage */}
        <Section labelledBy="coverage-heading" data-id="element-856" data-mp="1jdwt8w819b6t">
          <SectionHeading eyebrow="Reach" title={blockValue('home', 'coverage.heading')} data-id="element-857" data-mp="1jd586fc2avus" />
          <div className="mt-8 max-w-3xl" data-id="element-858" data-mp="1rsuh4payfhc5">
            <Placeholder note={coverageBlock?.placeholderNote} data-id="element-859" data-mp="1vvr38pzpdvku">
              <p className="font-serif text-[1.0625rem] leading-relaxed" data-id="element-860" data-mp="1vxbua5mt6wdy">{coverageBlock?.value}</p>
            </Placeholder>
          </div>
          <ul className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2" data-id="element-861" data-mp="1mv2qrzpyk3a2">
            {coverageRegions.map(r => <li key={r.id} className="border-t border-navy-100 pt-5" data-id="element-862" data-mp="2akn9chi78yit">
                <h3 className="font-serif text-[1.0625rem] text-ink" data-id="element-863" data-mp="2fk02tl3kt3ud">{r.label}</h3>
                <div className="mt-3" data-id="element-864" data-mp="2flt2uhjoopte">
                  <Placeholder data-id="element-865" data-mp="20vvlakg9eyd6">
                    <p className="font-sans text-[0.875rem] leading-relaxed" data-id="element-866" data-mp="20ukfkf397gjf">{r.detail}</p>
                  </Placeholder>
                </div>
              </li>)}
          </ul>
        </Section>

        {/* Research */}
        <Section tone="parchment" labelledBy="research-heading" data-id="element-867" data-mp="2yahjgrb4w8zd">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20" data-id="element-868" data-mp="2yc2d8epmjnw6">
            <div data-id="element-869" data-mp="y904ac9z205n">
              <GoldRule data-id="element-870" data-mp="y9je15pgxi8l" />
              <Eyebrow className="mt-5" data-id="element-871" data-mp="thi3bo7y7zko">Research</Eyebrow>
              <h2 id="research-heading" className="mt-3 font-serif text-[1.75rem] leading-[1.15] text-ink md:text-[2.25rem]" data-id="element-872" data-mp="tfynfyq7fqjf">
                {blockValue('home', 'research.heading')}
              </h2>
              <p className="mt-6 font-serif text-[1.0625rem] leading-relaxed text-charcoal-700" data-id="element-873" data-mp="kielsoofjs8a">
                {blockValue('home', 'research.body')}
              </p>
              <ul className="mt-8 space-y-4" data-id="element-874" data-mp="1c7rhzxkx9jt9">
                {searchPhilosophy.slice(0, 3).map(p => <li key={p.heading} className="border-l-2 border-gold pl-5" data-id="element-875" data-mp="185cxza5ioe0e">
                    <h3 className="font-serif text-[1rem] text-ink" data-id="element-876" data-mp="137unhfbupqqa">{p.heading}</h3>
                    <p className="mt-1.5 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-877" data-mp="3swn2ozhlaukm">{p.body}</p>
                  </li>)}
              </ul>
            </div>
            <img src={RESEARCH_IMAGE} alt="An empty executive boardroom in low light, with a long table and city windows beyond." width={1280} height={720} loading="lazy" className="h-full w-full object-cover" data-id="element-878" data-mp="3nzwnsfhiu50s" />
          </div>
        </Section>

        {/* Insights */}
        <Section labelledBy="insights-heading" data-id="element-879" data-mp="2h8h8fkro45vt">
          <SectionHeading eyebrow="Publications" title={blockValue('home', 'insights.heading')} data-id="element-880" data-mp="2m69a36i476d7" />
          <ul className="mt-12 grid gap-x-10 gap-y-9 md:grid-cols-3" data-id="element-881" data-mp="2r4u3alyy8ps5">
            {articles.slice(0, 3).map(a => <li key={a.slug} className="border-t border-navy-100 pt-5" data-id="element-882" data-mp="2w3x8q7iucy0n">
                <Link to={`/insights/${a.slug}`} className="group block" data-id="element-883" data-mp="305igxfkfko4i">
                  <Eyebrow data-id="element-884" data-mp="303o30l7ktw9x">{a.readingTimeMinutes} minute read</Eyebrow>
                  <h3 className="mt-3 font-serif text-[1.125rem] leading-snug text-ink group-hover:text-gold-700" data-id="element-885" data-mp="11rqka70xskqe">
                    {a.title}
                  </h3>
                  <p className="mt-2.5 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-886" data-mp="plj0vr3vb0kl">{a.standfirst}</p>
                  <p className="mt-4 font-sans text-[0.75rem] text-charcoal-300" data-id="element-887" data-mp="ul5dpzfsaesv">
                    {formatShortDate(a.publishedAt)} · Updated {formatShortDate(a.updatedAt)}
                  </p>
                </Link>
              </li>)}
          </ul>
          <ButtonLink to="/insights" variant="secondary" size="sm" className="mt-8" data-id="element-888" data-mp="1999zxde1p8ge">
            All insights
          </ButtonLink>
        </Section>

        {/* Testimonials */}
        <Section tone="parchment" labelledBy="testimonials-heading" data-id="element-889" data-mp="1dcg5sjx8wxmc">
          <SectionHeading eyebrow="References" title={blockValue('home', 'testimonials.heading')} data-id="element-890" data-mp="1ia7vd7af47nd" />
          <div className="mt-8 max-w-3xl" data-id="element-891" data-mp="1n89h5zz8al2j">
            <Placeholder note={testimonialsBlock?.placeholderNote} data-id="element-892" data-mp="3k1b6dxo7zo8g">
              <p className="font-serif text-[1.0625rem] leading-relaxed" data-id="element-893" data-mp="3k35v7n2m40ht">{testimonialsBlock?.value}</p>
            </Placeholder>
          </div>
        </Section>

        {/* Final CTA */}
        <Section tone="ink" labelledBy="cta-heading" data-id="element-894" data-mp="3hhy2y97z1r1l">
          <div className="max-w-2xl" data-id="element-895" data-mp="3cpdb3d8mlo0p">
            <GoldRule data-id="element-896" data-mp="3coc2dff31dm5" />
            <h2 id="cta-heading" className="mt-5 font-serif text-[1.75rem] leading-[1.15] text-parchment md:text-[2.5rem]" data-id="element-897" data-mp="3co28vhutwobp">
              {blockValue('home', 'cta.heading')}
            </h2>
            <p className="mt-5 font-serif text-[1.0625rem] leading-relaxed text-navy-100" data-id="element-898" data-mp="32pmhddrw3d1o">
              {blockValue('home', 'cta.body')}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-899" data-mp="3vg4pit7h04ly">
              <ButtonLink to="/contact" size="lg" data-id="element-900" data-mp="3qhbh3f71j3sn">
                Contact the firm
              </ButtonLink>
              <ButtonLink to="/register" size="lg" variant="onDark" data-id="element-901" data-mp="3liqcl3kr9xye">
                Register as an Executive
              </ButtonLink>
            </div>
          </div>
        </Section>
      </main>
    </>;
}